<template>
  <div class="mt-1">
    <b-card>
      <b-overlay :show="loading" rounded="sm" no-fade>
        <b-row class="mb-1">
          <b-col
            cols="12"
            md="7"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <h3 class="ml-1">Events</h3>
          </b-col>
          <b-col cols="12" md="5">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="filter"
                class="d-inline-block mr-1"
                placeholder="Search..."
              />
              <b-button
                v-if="canCreateEvent()"
                variant="primary"
                @click="addEvent()"
                ><span class="text-nowrap">Add Event</span></b-button
              >
            </div>
          </b-col>
        </b-row>

        <b-table
          sticky-header
          striped
          hover
          bordered
          ref="refEventListTable"
          :outlined="true"
          :fields="fields"
          :items="filtered"
          responsive="sm"
          :small="true"
          class="table table-sm table-responsive"
          show-empty
          empty-text="No matching records found"
          :tbody-tr-class="rowClass"
          @row-clicked="view($event.id)"
        >
          <template #cell(created_by_name)="{ item }">
            <div style="min-width: 140px;">
              <span>{{ item.created_by_name || "-" }}</span>
            </div>
          </template>
          <template #cell(course)="{ item }">
            <div style="max-width: 400px;" class="p-25">
              <p class="mb-0 font-weight-600">
                {{ item.course_title || (item.course && item.course.title) }}
              </p>
              <small
                class="text-muted text-truncate d-block"
                v-if="item.title_override"
                :title="item.title_override"
                >{{ item.title_override }}</small
              >

              <div v-if="item && item.is_eft" class="mt-25">
                <b-badge variant="light-info">Educational Fee Training</b-badge>
              </div>
            </div>
          </template>
          <template #cell(dates)="{ item }">
            <div style="min-width: 150px;">
              <!-- Show each session/part separately -->
              <div v-if="item.sessions && item.sessions.length > 0">
                <div
                  v-for="(session, index) in item.sessions"
                  :key="session.id"
                  class="mb-1"
                  style="font-size: 0.85em;"
                >
                  <strong>Part {{ index + 1 }}:</strong><br />
                  <span>{{ formatDate(session.start_at) }}</span>
                  <span
                    v-if="session.end_at && session.start_at !== session.end_at"
                  >
                    - {{ formatDate(session.end_at) }}
                  </span>
                </div>
              </div>
              <!-- Fallback to date_range if no sessions -->
              <div
                v-else-if="
                  item.date_range &&
                    (item.date_range.start || item.date_range.end)
                "
              >
                <span>{{ formatDate(item.date_range.start) }}</span>
                <span v-if="item.date_range.end">
                  - {{ formatDate(item.date_range.end) }}</span
                >
              </div>
              <div v-else>-</div>
            </div>
          </template>
          <template #cell(venue)="{ item }">
            <div style="min-width: 100px;">
              <!-- Show venue for each session/part -->
              <div v-if="item.sessions && item.sessions.length > 0">
                <div
                  v-for="(session, index) in item.sessions"
                  :key="session.id"
                  class="mb-1"
                  style="font-size: 0.85em;"
                >
                  <strong>Part {{ index + 1 }}:</strong><br />
                  <span>{{ session.venue || "-" }}</span>
                </div>
              </div>
              <!-- Fallback to first session venue -->
              <div v-else>
                <span>{{
                  (firstSession(item) && firstSession(item).venue) || "-"
                }}</span>
              </div>
            </div>
          </template>
          <template #cell(time)="{ item }">
            <div style="min-width: 120px;">
              <!-- Show time for each session/part -->
              <div v-if="item.sessions && item.sessions.length > 0">
                <div
                  v-for="(session, index) in item.sessions"
                  :key="session.id"
                  class="mb-1"
                  style="font-size: 0.85em;"
                >
                  <strong>Part {{ index + 1 }}:</strong><br />
                  <span v-if="session.start_at">
                    {{ formatTime(session.start_at) }}
                    <span v-if="session.end_at">
                      - {{ formatTime(session.end_at) }}</span
                    >
                  </span>
                  <span v-else>-</span>
                </div>
              </div>
              <!-- Fallback to first session time -->
              <div v-else>
                <span v-if="firstSession(item)"
                  >{{ formatTime(firstSession(item).start_at) }} -
                  {{ formatTime(firstSession(item).end_at) }}</span
                >
                <span v-else>-</span>
              </div>
            </div>
          </template>
          <template #cell(trainer)="{ item }">
            <div style="min-width: 100px;">
              <!-- Show trainer for each session/part -->
              <div v-if="item.sessions && item.sessions.length > 0">
                <div
                  v-for="(session, index) in item.sessions"
                  :key="session.id"
                  class="mb-1"
                  style="font-size: 0.85em;"
                >
                  <strong>Part {{ index + 1 }}:</strong><br />
                  <span>{{ session.trainer || "-" }}</span>
                </div>
              </div>
              <!-- Fallback to first session trainer -->
              <div v-else>
                <span>{{
                  (firstSession(item) && firstSession(item).trainer) || "-"
                }}</span>
              </div>
            </div>
          </template>
          <template #cell(max)="{ item }">
            <span
              >{{ item.accepted_count || 0 }}/{{ item.max_participants }}</span
            >
          </template>
          <template #cell(status)="{ item }">
            <b-badge :variant="statusVariant(item.status)">{{
              item.status
            }}</b-badge>
          </template>
          <template #cell(actions)="{ item }">
            <div>
              <b-button
                variant="outline-primary"
                class="mr-25 p-50"
                @click.stop="
                  $router.push({ name: 'event-view', params: { id: item.id } })
                "
                ><i class="fa-solid fa-eye mr-25"></i
                ><span> View</span></b-button
              >
              <b-button
                v-if="canEditEvent(item)"
                variant="outline-secondary"
                class="mr-25 p-50"
                @click.stop="
                  $router.push({
                    name: 'event-applications',
                    params: { id: item.id },
                  })
                "
                ><i class="fa-solid fa-users mr-25"></i
                ><span> Applications</span></b-button
              >
              <b-button
                v-if="canEditEvent(item)"
                variant="warning"
                class="mr-25 p-50"
                @click.stop="edit(item.id)"
                ><i class="fa-solid fa-pen-to-square mr-25"></i
                ><span> Edit</span></b-button
              >
              <b-button
                v-if="canDuplicateEvent(item)"
                variant="info"
                class="mr-25 p-50"
                @click.stop="duplicate(item.id)"
                ><i class="fa-solid fa-copy mr-25"></i
                ><span> Duplicate</span></b-button
              >
              <b-button
                v-if="canDeleteEvent(item)"
                variant="danger"
                class="p-50"
                @click.stop="remove(item.id)"
                ><span>Delete</span></b-button
              >
            </div>
          </template>
        </b-table>

        <div class="mx-2 mb-1 mt-1">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted"
                >Showing {{ dataMeta.from }} to {{ dataMeta.to }} of
                {{ dataMeta.of }} entries</span
              >
            </b-col>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text
                  ><feather-icon icon="ChevronLeftIcon" size="18"
                /></template>
                <template #next-text
                  ><feather-icon icon="ChevronRightIcon" size="18"
                /></template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-overlay>
    </b-card>
  </div>
</template>
<script>
import {
  BOverlay,
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BBadge,
  BPagination,
} from "bootstrap-vue";
import store from "@/store";
import { onUnmounted } from "@vue/composition-api";
import eventsStoreModule from "./eventsStoreModule";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  components: {
    BOverlay,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BBadge,
    BPagination,
    ToastificationContent,
  },
  data() {
    return {
      loading: false,
      items: [],
      filter: null,
      currentPage: 1,
      perPage: 50,
      totalRows: 0,
      fields: [
        { key: "course", label: "Course", thStyle: "color:white!important" },
        {
          key: "created_by_name",
          label: "Created By",
          thStyle: "color:white!important",
        },
        { key: "dates", label: "Date(s)", thStyle: "color:white!important" },
        { key: "venue", label: "Venue", thStyle: "color:white!important" },
        { key: "time", label: "Time", thStyle: "color:white!important" },
        { key: "trainer", label: "Trainer", thStyle: "color:white!important" },
        {
          key: "max",
          label: "Max",
          class: "text-center",
          thStyle: "color:white!important",
        },
        {
          key: "status",
          label: "Event Status",
          class: "text-center",
          thStyle: "color:white!important",
        },
        {
          key: "actions",
          label: "Actions",
          class: "text-center",
          thStyle: "color:white!important",
        },
      ],
    };
  },
  setup() {
    const MODULE = "events";
    if (!store.hasModule(MODULE))
      store.registerModule(MODULE, eventsStoreModule);
    onUnmounted(() => {
      if (store.hasModule(MODULE)) store.unregisterModule(MODULE);
    });
  },
  created() {
    this.loadAllEvents();
  },
  computed: {
    dataMeta() {
      return {
        from: this.totalRows ? this.perPage * (this.currentPage - 1) + 1 : 0,
        to: Math.min(
          this.perPage * (this.currentPage - 1) + this.perPage,
          this.totalRows
        ),
        of: this.totalRows,
      };
    },
    filtered() {
      if (!this.filter) return this.items;
      const t = this.filter.toLowerCase();
      return this.items.filter(
        (i) =>
          (i.course_title || (i.course && i.course.title) || "")
            .toLowerCase()
            .includes(t) || (i.aim || "").toLowerCase().includes(t)
      );
    },
  },
  watch: {
    currentPage(newPage) {
      this.loadAllEvents();
    },
    perPage(newPerPage) {
      this.currentPage = 1;
      this.loadAllEvents();
    },
  },
  methods: {
    rowClass(item, type) {
      if (type !== 'row' || !item) return '';
      return item.is_eft ? 'eft-row' : '';
    },
    loadAllEvents() {
      this.loading = true;
      store
        .dispatch("events/fetchEventsAllYears", {
          page: this.currentPage,
          per_page: this.perPage,
        })
        .then((res) => {
          this.items = Array.isArray(res.data) ? res.data : [];
          // The server paginates and reports the real total in X-Total-Count.
          // The table itself must not paginate again - it only ever holds one
          // page, so a second slice on page 2+ would show nothing.
          const total = Number(res.headers && res.headers["x-total-count"]);
          if (Number.isFinite(total) && total >= 0) {
            this.totalRows = total;
          } else {
            // Header missing (older API / not exposed by CORS): fall back to
            // guessing one more item exists whenever the page came back full.
            const count = this.items.length;
            const base = (this.currentPage - 1) * this.perPage;
            this.totalRows = count === this.perPage ? base + count + 1 : base + count;
          }
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Failed to load events",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    addEvent() {
      this.$router.push({ name: "event-add" });
    },
    edit(id) {
      this.$router.push({ name: "event-edit", params: { id } });
    },
        view(id) {
      this.$router.push({ name: "event-view", params: { id } });
    },
    duplicate(id) {
      this.$router.push({ name: "event-duplicate", params: { id } });
    },
    remove(id) {
      const item = this.items.find(i => i.id === id);

      this.$bvModal
        .msgBoxConfirm("Delete this event?", {
          size: "sm",
          title: "Confirm",
          okVariant: "primary",
          okTitle: "Yes",
          cancelTitle: "No",
          cancelVariant: "outline-secondary",
          centered: true,
        })
        .then((v) => {
          if (!v) return;
          this.loading = true;
          store
            .dispatch("events/deleteEvent", { id })
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Deleted",
                  icon: "ThumbsUpIcon",
                  variant: "success",
                },
              });
              this.loadAllEvents();
            })
            .catch(() => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Delete failed",
                  icon: "AlertTriangleIcon",
                  variant: "danger",
                },
              });
              this.loading = false;
            });
        });
    },
    firstSession(item) {
      return (item.sessions && item.sessions[0]) || null;
    },
    statusVariant(s) {
      return s === "open"
        ? "light-success"
        : s === "closed"
        ? "light-secondary"
        : s === "cancelled"
        ? "light-danger"
        : "light-warning";
    },
    formatDate(val) {
      if (!val) return "-";
      const date = new Date(val);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },
    formatTime(val) {
      if (!val) return "-";
      const d = new Date(val);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
    },
    isManagerOrAdmin() {
      const userData = JSON.parse(localStorage.getItem("userData")) || {};
      const userRole = userData.role || "";
      // Department Manager'ı kaldırdık - sadece gerçek admin roller
      return ["admin", "supermanager", "Administrator"].includes(userRole);
    },

    // Permission Methods - Mevcut admin/manager yapısını koruyarak yeni permission sistemini entegre eder
    canCreateEvent() {
      // Mevcut admin/manager yapısını koru VEYA yeni permission kontrol et
      return this.isManagerOrAdmin() || this.$Can("create_event");
    },

    canEditEvent(item) {
      // Event-specific: edit allowed only for event creator (plus system admins)
      return this.$CanEvent("edit_event", item || {});
    },

    canDeleteEvent(item) {
      const admins = (item && (item.admins || item.event_admins)) || item || [];
      return item && (item.status === "draft" || this.isManagerOrAdmin()) && this.$CanEvent("delete_event", admins);
    },

    canDuplicateEvent(item) {
      const admins = (item && (item.admins || item.event_admins)) || item || [];
      return this.$CanEvent("duplicate_event", admins);
    },

    canViewApplications(item = null) {
      // Allow if system admin, event admin, or has permission
      const admins = item ? (item.admins || item.event_admins || []) : [];
      return this.$CanEvent("view_event_applications", admins);
    },
  },
};
</script>
<style scoped>
.table-responsive.b-table-sticky-header {
  max-height: 75vh !important;
}

::v-deep tr.eft-row {
  background-color: #e7f3ff !important;
}

::v-deep tr.eft-row:hover {
  background-color: #dbefff !important;
}
</style>
